import React, { useEffect, useMemo } from 'react';
import { Page } from '../../types';
import { useAppContext } from '../../contexts/AppContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { AppIcon, IconName } from '../icons/AppIcon';
import { cn } from '@/lib/utils';
import {
    LAYOUT_NAV_SECTION_LABEL_CLASS,
    MOBILE_CONTENT_BOTTOM_PAD_CLASS,
    SETTINGS_NAV_ITEM_CLASS,
    cnLayoutNavIcon,
    cnLayoutNavRow,
} from './layoutNavTokens';
import {
    buildSettingsNavSections,
    type SettingsNavSection,
} from './sidebar/config';

function useSettingsSections() {
    const { isMember, canViewBilling } = usePermissions();
    return useMemo(
        () => buildSettingsNavSections(isMember, canViewBilling),
        [isMember, canViewBilling],
    );
}

function SettingsNavButton({
    label,
    icon,
    onClick,
    isActive = false,
    danger = false,
}: {
    label: string;
    icon: IconName;
    onClick: () => void;
    isActive?: boolean;
    danger?: boolean;
}) {
    return (
        <button
            type="button"
            aria-current={isActive ? 'page' : undefined}
            onClick={onClick}
            className={cn(
                SETTINGS_NAV_ITEM_CLASS,
                danger
                    ? 'text-text-secondary transition-colors duration-150 hover:bg-danger-bg hover:text-danger-text active:bg-danger-bg active:text-danger-text'
                    : cn('md:min-h-0', cnLayoutNavRow(isActive)),
            )}
        >
            <AppIcon
                name={icon}
                className={cn('h-4 w-4 shrink-0', !danger && cnLayoutNavIcon(isActive))}
                aria-hidden
            />
            {label}
        </button>
    );
}

function SettingsNavList({
    sections,
    activeTab,
    onSelect,
    idPrefix = '',
}: {
    sections: SettingsNavSection[];
    activeTab?: Page;
    onSelect: (page: Page) => void;
    idPrefix?: string;
}) {
    return (
        <>
            {sections.map((section, idx) => {
                const headingId = `${idPrefix}settings-section-${section.title.toLowerCase().replace(/\s+/g, '-')}`;
                return (
                    <section
                        key={section.title}
                        aria-labelledby={headingId}
                        className={cn(idx > 0 && 'mt-s6')}
                    >
                        <h2 id={headingId} className={cn(LAYOUT_NAV_SECTION_LABEL_CLASS, 'mb-s1 px-s4')}>
                            {section.title}
                        </h2>
                        <nav aria-labelledby={headingId}>
                            <ul className="m-0 flex list-none flex-col gap-0.5 px-s2 py-0">
                                {section.items.map((item) => (
                                    <li key={item.id}>
                                        <SettingsNavButton
                                            label={item.label}
                                            icon={item.icon}
                                            isActive={activeTab === item.id}
                                            onClick={() => onSelect(item.id)}
                                        />
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </section>
                );
            })}
        </>
    );
}

/** Índice full-page (mobile). Desktop redireciona para Meus Dados. */
export const SettingsHub: React.FC = () => {
    const { setCurrentPage, goBack } = useAppContext();
    const { logout } = useAuth();
    const sections = useSettingsSections();

    useEffect(() => {
        const mq = window.matchMedia('(min-width: 768px)');
        const sync = () => {
            if (mq.matches) setCurrentPage('profile');
        };
        sync();
        mq.addEventListener('change', sync);
        return () => mq.removeEventListener('change', sync);
    }, [setCurrentPage]);

    return (
        <div className="flex h-full flex-col bg-card md:hidden">
            <header className="flex shrink-0 items-start justify-between gap-s3 border-b border-subtle px-s4 py-s4">
                <div className="min-w-0">
                    <h1 id="settings-hub-title" className="text-lg font-semibold text-text">
                        Configurações
                    </h1>
                    <p className="mt-s1 text-sm text-text-secondary">
                        Escolha uma seção para gerenciar.
                    </p>
                </div>
                <button
                    type="button"
                    aria-label="Fechar"
                    onClick={goBack}
                    className={cn(
                        'flex size-11 shrink-0 items-center justify-center rounded-lg text-text-muted touch-manipulation',
                        'transition-colors duration-150 hover:bg-surface-subtle hover:text-text',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    )}
                >
                    <AppIcon name="close" className="h-4 w-4" aria-hidden />
                </button>
            </header>
            <div className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain py-s4', MOBILE_CONTENT_BOTTOM_PAD_CLASS)}>
                <SettingsNavList sections={sections} onSelect={setCurrentPage} idPrefix="hub-" />
                <footer className="mt-s6 border-t border-subtle px-s2 pt-s4">
                    <SettingsNavButton
                        label="Sair"
                        icon="logout"
                        danger
                        onClick={() => void logout()}
                    />
                </footer>
            </div>
        </div>
    );
};

export const SettingsLayout: React.FC<{ children: React.ReactNode; activeTab: Page }> = ({
    children,
    activeTab,
}) => {
    const { setCurrentPage } = useAppContext();
    const sections = useSettingsSections();
    const activeItem =
        sections.flatMap((s) => s.items).find((item) => item.id === activeTab) ??
        sections[0]?.items[0];

    return (
        <div className="flex h-full flex-col md:flex-row">
            <header className="sticky top-0 z-20 shrink-0 border-b border-subtle bg-card px-s4 py-s3 md:hidden">
                <button
                    type="button"
                    aria-label="Abrir Configurações"
                    onClick={() => setCurrentPage('settings')}
                    className={cn(
                        'flex min-h-11 w-full items-center gap-s3 rounded-xl border border-subtle bg-surface-subtle px-s3 py-s2 text-left touch-manipulation',
                        'transition-colors duration-150 hover:bg-primary-subtle',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                    )}
                >
                    {activeItem ? (
                        <AppIcon name={activeItem.icon} className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                    ) : null}
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-text">
                        {activeItem?.label ?? 'Configurações'}
                    </span>
                    <AppIcon name="chevron-down" className="h-4 w-4 shrink-0 text-text-muted" aria-hidden />
                </button>
            </header>

            <aside
                aria-label="Menu de configurações"
                className="hidden w-52 shrink-0 flex-col overflow-y-auto border-r border-subtle bg-card py-s6 md:flex"
            >
                <SettingsNavList sections={sections} activeTab={activeTab} onSelect={setCurrentPage} />
            </aside>

            <div className={cn('min-w-0 flex-1 overflow-y-auto p-s4 md:p-6 md:pb-16', MOBILE_CONTENT_BOTTOM_PAD_CLASS)}>
                {children}
            </div>
        </div>
    );
};
